import { Flex, Text, Tooltip, useDisclosure } from "@chakra-ui/react";
import { Layout } from "@/components/Layout/Layout";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { Pagination } from "@/components/Pagination/Pagination";
import { createColumnHelper } from "@tanstack/react-table";
import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/helpers/formatDate";
import { mockPartners, Partner } from "./mockData";
import { useSessionStorageFilters } from "@/hooks/useSessionStorageFilters";
import { PartnersFilter, defaultPartnersFilterFields } from "./_helpers";
import { PartnersPopupFilter } from "./components/PartnersPopupFilter";
import { Recruiter } from "@/model/recruiters";
import api from "@/api";

export const PartnersPage = () => {
  const navigate = useNavigate();
  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [recruiters, setRecruiters] = useState<readonly Recruiter[]>([]);

  const { formik, handleSearch, handleApplyFilters, handleResetFilters } = useSessionStorageFilters<PartnersFilter>({
    storageKey: "partnersPageFilters",
    defaultValues: defaultPartnersFilterFields,
  });

  const columnHelper = createColumnHelper<Partner>();

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: "Название",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="200px" fontSize={14} fontWeight={600} color="#3b515e">
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 200,
      minSize: 150,
    }),
    columnHelper.accessor("contacts", {
      id: "contacts",
      header: "Контакты",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="250px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 250,
      minSize: 200,
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: "Статус",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 100,
    }),
    columnHelper.accessor("responsible", {
      id: "responsible",
      header: "Ответственный",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="150px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 150,
      minSize: 120,
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Дата создания",
      cell: (info) => (
        <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {formatDate(info.getValue() || "")}
          </Text>
        </Tooltip>
      ),
      enableSorting: true,
      size: 120,
      minSize: 100,
    }),
  ];

  // Получаем уникальные статусы из mockPartners
  const statusOptions = useMemo(() => {
    const uniqueStatuses = Array.from(new Set(mockPartners.map((p) => p.status)));
    return uniqueStatuses.map((status) => ({ value: status, label: status }));
  }, []);

  // Загрузка списка рекрутеров
  useEffect(() => {
    let isMounted = true;
    api.recruiters
      .recruiters({})
      .then(({ data }) => {
        if (isMounted) setRecruiters(data || []);
      })
      .catch(() => {
        if (isMounted) setRecruiters([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Сброс страницы при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [formik.values.status, formik.values.responsible, formik.values.created_at_from, formik.values.created_at_to, formik.values.search]);

  // Фильтрация данных на основе поиска и фильтров
  const filteredPartners = useMemo(() => {
    return mockPartners.filter((partner) => {
      // Поиск
      const matchesSearch =
        !formik.values.search ||
        partner.name.toLowerCase().includes(formik.values.search.toLowerCase()) ||
        partner.contacts.toLowerCase().includes(formik.values.search.toLowerCase()) ||
        partner.status.toLowerCase().includes(formik.values.search.toLowerCase()) ||
        partner.responsible.toLowerCase().includes(formik.values.search.toLowerCase());

      // Фильтр по статусу
      const matchesStatus = !formik.values.status || partner.status === formik.values.status;

      // Фильтр по ответственному
      const matchesResponsible = !formik.values.responsible || partner.responsible === formik.values.responsible;

      // Фильтр по дате создания
      let matchesDateFrom = true;
      let matchesDateTo = true;

      if (formik.values.created_at_from) {
        const filterDateFrom = new Date(formik.values.created_at_from);
        const partnerDate = new Date(partner.created_at);
        matchesDateFrom = partnerDate >= filterDateFrom;
      }

      if (formik.values.created_at_to) {
        const filterDateTo = new Date(formik.values.created_at_to);
        filterDateTo.setHours(23, 59, 59, 999); // Устанавливаем конец дня
        const partnerDate = new Date(partner.created_at);
        matchesDateTo = partnerDate <= filterDateTo;
      }

      return matchesSearch && matchesStatus && matchesResponsible && matchesDateFrom && matchesDateTo;
    });
  }, [formik.values]);

  // Пагинация данных
  const paginatedPartners = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPartners.slice(startIndex, endIndex);
  }, [filteredPartners, currentPage, itemsPerPage]);

  const totalItems = filteredPartners.length;

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (formik.values.status) count += 1;
    if (formik.values.responsible) count += 1;
    if (formik.values.created_at_from) count += 1;
    if (formik.values.created_at_to) count += 1;
    return count;
  }, [formik.values.status, formik.values.responsible, formik.values.created_at_from, formik.values.created_at_to]);

  const handlePageChange = (offset: number, limit: number) => {
    const newPage = offset / limit + 1;
    setCurrentPage(newPage);
    setItemsPerPage(limit);
  };

  const handleCreate = () => {
    navigate("/partners/create");
  };

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Text fontSize={27} fontWeight={500}>
          Партнеры
        </Text>

        {/* Контейнер для фильтров */}
        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по партнерам"
          searchValue={formik.values.search || ""}
          isShowBtnPopupFilters
          selectedFiltersCount={appliedFiltersCount}
          onClickBtnPopupFilters={onFiltersOpen}
          isOpenPopupFilters={isFiltersOpen}
          onClosePopupFilters={onFiltersClose}
          popupFilters={
            <PartnersPopupFilter
              values={formik.values}
              onApply={(v) => {
                handleApplyFilters(v);
                onFiltersClose();
              }}
              onReset={() => {
                handleResetFilters();
                onFiltersClose();
              }}
              recruiters={recruiters}
              statusOptions={statusOptions}
            />
          }
          isShowCreateButton={true}
          onClickCreate={handleCreate}
          isLoading={false}
        />

        {/* Таблица */}
        <BaseTable list={paginatedPartners} columns={columns} isLoading={false} clickHandler={(id) => navigate(`/partners/${id}`)} />

        {/* Пагинация */}
        <Pagination totalItems={totalItems} onPageChange={handlePageChange} isLoading={false} />
      </Flex>
    </Layout>
  );
};
